import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, Input, Select, EmptyState } from "../components/ui-kit";
import { Users2, TrendingUp, IndianRupee, ShoppingCart, MapPin, ChevronRight } from "lucide-react";
import { thisMonthIST } from "../lib/datefmt";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const num = (n) => Number(n || 0).toLocaleString("en-IN");

function KpiCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#1E3F20]/10 grid place-items-center text-[#1E3F20]">
          <Icon className="w-5 h-5" strokeWidth={2.25} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#5C6B5E]">{label}</div>
          <div className="font-heading text-2xl font-black text-[#1A2E1C] leading-tight mt-1">{value}</div>
          {sub && <div className="text-xs text-[#5C6B5E] mt-0.5">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

function AchievementBar({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-[#5C6B5E]">—</span>;
  const capped = Math.min(pct, 100);
  const color = pct >= 100 ? "bg-[#1E3F20]" : pct >= 70 ? "bg-[#A3B18A]" : "bg-[#DDA15E]";
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2 bg-[#E5E2DC] rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${capped}%` }} />
      </div>
      <div className="text-xs font-semibold text-[#1A2E1C] whitespace-nowrap">{pct}%</div>
    </div>
  );
}

export default function TeamPerformancePage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(() => thisMonthIST());
  const [data, setData] = useState({ members: [], totals: null });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("cumulative"); // 'cumulative' | member user_id

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/team/performance?period=${period}`);
        if (!cancelled) setData(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const allMembers = data.members || [];
  const cumulativeTotals = data.totals || {};

  const isCumulative = view === "cumulative";
  const selectedMember = allMembers.find((m) => m.user_id === view);

  // KPIs come from either the cumulative totals or the selected member's row.
  const totals = isCumulative ? cumulativeTotals : (selectedMember || {});
  const members = isCumulative ? allMembers : selectedMember ? [selectedMember] : [];

  const roleLabel = useMemo(
    () => (r) => (r || "").replace(/_/g, " "),
    []
  );

  return (
    <div>
      <PageHeader
        title="Team Performance"
        subtitle="Cumulative KPIs for everyone reporting to you (all descendants)"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={view}
              onChange={(e) => setView(e.target.value)}
              className="w-56"
              data-testid="team-perf-view"
            >
              <option value="cumulative">Reporting Team (cumulative)</option>
              {allMembers.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.user_name}</option>
              ))}
            </Select>
            <Input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-44"
              data-testid="team-perf-period"
            />
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon={ShoppingCart}
          label="Orders (this period)"
          value={num(totals.orders_count)}
          sub={`Value ${money(totals.orders_value)}`}
          testid="kpi-orders"
        />
        <KpiCard
          icon={IndianRupee}
          label="Collections"
          value={money(totals.collections_amount)}
          sub={`${num(totals.collections_count)} entries`}
          testid="kpi-collections"
        />
        <KpiCard
          icon={TrendingUp}
          label="Order achievement"
          value={totals.order_achievement_pct !== null && totals.order_achievement_pct !== undefined ? `${totals.order_achievement_pct}%` : "—"}
          sub={`${num(totals.order_achieved_qty)} / ${num(totals.order_target_qty)} target qty`}
          testid="kpi-order-ach"
        />
        <KpiCard
          icon={MapPin}
          label="Field activity"
          value={`${num(totals.field_days)} days`}
          sub={`${num(totals.km_covered)} km covered`}
          testid="kpi-field"
        />
      </div>

      <Card className="p-0 overflow-hidden" data-testid="team-perf-table-card">
        <div className="p-4 border-b border-[#E5E2DC] flex items-center gap-2">
          <Users2 className="w-4 h-4 text-[#1E3F20]" />
          <div className="font-heading text-lg font-bold">
            {isCumulative ? `Team members (${allMembers.length})` : `Detail — ${selectedMember?.user_name || ""}`}
          </div>
          {loading && <div className="text-xs text-[#5C6B5E] ml-auto">Loading…</div>}
        </div>
        {members.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No team members yet" description="Assign users to report to you from the Users page to see their rolled-up numbers here." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Orders</th>
                  <th className="px-4 py-3">Order value</th>
                  <th className="px-4 py-3">Order achievement</th>
                  <th className="px-4 py-3">Collections</th>
                  <th className="px-4 py-3">Coll. achievement</th>
                  <th className="px-4 py-3">Field days</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr
                    key={m.user_id}
                    className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60 cursor-pointer group"
                    data-testid={`team-row-${m.user_id}`}
                    onClick={() => navigate(`/team/${m.user_id}?period=${period}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold group-hover:text-[#1E3F20]">{m.user_name}</div>
                      <div className="text-xs text-[#5C6B5E]">{m.email}{m.team_code ? ` · ${m.team_code}` : ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#5C6B5E]">
                        {roleLabel(m.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold">{num(m.orders_count)}</td>
                    <td className="px-4 py-3">{money(m.orders_value)}</td>
                    <td className="px-4 py-3"><AchievementBar pct={m.order_achievement_pct} /></td>
                    <td className="px-4 py-3">{money(m.collections_amount)}</td>
                    <td className="px-4 py-3"><AchievementBar pct={m.collection_achievement_pct} /></td>
                    <td className="px-4 py-3">{num(m.field_days)}</td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-[#5C6B5E] group-hover:text-[#1E3F20]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
